import React, { useState, useEffect, useRef } from 'react';
import {
  Trash2, Search, Eye, MapPin, ArrowLeft,
  X, Printer, CheckCircle, XCircle,
  RotateCcw, QrCode, Minus, Plus, Upload, FileCheck, Download, Trash
} from 'lucide-react';
import { showGlobalNotification } from '../hooks/useNotifications';
import QRCodeLib from 'qrcode';

const API_BASE_URL = import.meta.env.VITE_API_URL;

interface ScrapForm {
  id: number;
  form_number: string;
  computer_id: number;
  device_type: 'laptop' | 'desktop' | 'workstation' | 'server';
  inventory_number: string;
  brand: string;
  model: string;
  serial_number: string;
  purchase_date: string | null;
  location_name: string | null;
  company_name: string | null;
  previous_user_name: string | null;
  hardware_checklist: {
    cpu: { status: string; detail: string; spare_part: boolean };
    ram: { status: string; detail: string; spare_part: boolean };
    storage: { status: string; detail: string; spare_part: boolean };
    screen: { status: string; detail: string; spare_part: boolean };
    motherboard: { status: string; detail: string; spare_part: boolean };
    battery: { status: string; detail: string; spare_part: boolean };
    adapter: { status: string; detail: string; spare_part: boolean };
    case_condition: { status: string; detail: string; spare_part: boolean };
    keyboard: { status: string; detail: string; spare_part: boolean };
    ports: { status: string; detail: string; spare_part: boolean };
  };
  scrap_reasons: string[];
  scrap_reason_detail: string | null;
  disk_removed: boolean;
  data_wipe_method: string;
  media_check_done: boolean;
  data_security_notes: string | null;
  photos: string[];
  created_by_user_id: number;
  created_by_user_name: string;
  scrap_date: string;
  created_at: string;
  signed_pdf_path: string | null;
  signed_pdf_uploaded_at: string | null;
  signed_pdf_uploaded_by: string | null;
}

interface Location {
  id: number;
  name: string;
}

interface ScrapStats {
  total: number;
  this_month: number;
  by_location: { location_name: string; count: number }[];
  by_reason: { [key: string]: number };
}

const ScrapComputersPage: React.FC = () => {
  const navigateTo = (page: string) => {
    window.dispatchEvent(new CustomEvent('navigate', { detail: page }));
  };
  const [scrapForms, setScrapForms] = useState<ScrapForm[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [stats, setStats] = useState<ScrapStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState<ScrapForm | null>(null);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [formToRestore, setFormToRestore] = useState<ScrapForm | null>(null);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedQRForm, setSelectedQRForm] = useState<ScrapForm | null>(null);
  const [qrSize, setQrSize] = useState(200);
  const [qrDataURL, setQrDataURL] = useState<string>('');
  const qrPrintRef = useRef<HTMLDivElement>(null);
  const [showSignedPdfModal, setShowSignedPdfModal] = useState(false);
  const [selectedSignedPdfForm, setSelectedSignedPdfForm] = useState<ScrapForm | null>(null);
  const [signedPdfFile, setSignedPdfFile] = useState<File | null>(null);
  const [signedPdfUploading, setSignedPdfUploading] = useState(false);

  useEffect(() => {
    loadData();
  }, [locationFilter, dateFrom, dateTo, searchTerm]);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');

      // Build query params
      const params = new URLSearchParams();
      if (locationFilter !== 'all') params.append('location_id', locationFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (searchTerm) params.append('search', searchTerm);

      const [formsRes, locationsRes, statsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/computers/scrapped/list?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/locations/dropdown`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/computers/scrap-stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const [formsData, locationsData, statsData] = await Promise.all([
        formsRes.json(),
        locationsRes.json(),
        statsRes.json()
      ]);

      setScrapForms(Array.isArray(formsData) ? formsData : []);
      setLocations(Array.isArray(locationsData) ? locationsData : []);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading data:', error);
      showGlobalNotification.error('Hata', 'Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: { [key: string]: string } = {
      exists: 'Var',
      missing: 'Yok',
      removed: 'Söküldü',
      good: 'İyi',
      broken: 'Kırık',
      dead_pixel: 'Ölü Piksel',
      stain: 'Lekeli',
      na: 'N/A',
      working: 'Çalışıyor',
      faulty: 'Arızalı',
      short_circuit: 'Kısa Devre',
      swollen: 'Şişmiş',
      not_charging: 'Şarj Tutmuyor',
      incomplete: 'Eksik',
      oxidized: 'Oksitlenmiş'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      exists: 'text-green-600',
      good: 'text-green-600',
      working: 'text-green-600',
      missing: 'text-red-600',
      removed: 'text-orange-600',
      broken: 'text-red-600',
      faulty: 'text-red-600',
      short_circuit: 'text-red-600',
      swollen: 'text-red-600',
      dead_pixel: 'text-yellow-600',
      stain: 'text-yellow-600',
      oxidized: 'text-yellow-600',
      not_charging: 'text-orange-600',
      incomplete: 'text-orange-600',
      na: 'text-gray-400'
    };
    return colors[status] || 'text-gray-600 dark:text-gray-400';
  };

  const getReasonLabel = (reason: string) => {
    const labels: { [key: string]: string } = {
      physical_damage: 'Fiziksel Hasar',
      obsolete: 'Teknolojik Eskime',
      repair_cost: 'Ekonomik Tamir Sınırı',
      parts_unavailable: 'Parça Tedarik Edilemiyor',
      other: 'Diğer'
    };
    return labels[reason] || reason;
  };

  const getDataWipeMethodLabel = (method: string) => {
    const labels: { [key: string]: string } = {
      software_wipe: 'Yazılımsal Wipe',
      physical_drill: 'Fiziksel Delme',
      degauss: 'Degauss',
      not_wiped: 'İmha Edilmedi',
      na: 'Uygulanmadı'
    };
    return labels[method] || method;
  };

  const getDeviceTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      laptop: 'Laptop',
      desktop: 'Desktop',
      workstation: 'Workstation',
      server: 'Server'
    };
    return labels[type] || type;
  };

  useEffect(() => {
    if (selectedQRForm) {
      const url = `${API_BASE_URL}/computers/public/scrap/${selectedQRForm.id}`;
      QRCodeLib.toDataURL(url, { width: qrSize * 2, margin: 2, errorCorrectionLevel: 'M' })
        .then(dataURL => setQrDataURL(dataURL))
        .catch(err => console.error('QR kod oluşturma hatası:', err));
    }
  }, [selectedQRForm, qrSize]);

  const handleQRPrint = () => {
    if (!selectedQRForm || !qrDataURL) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>QR Kod - ${selectedQRForm.inventory_number}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { display: flex; justify-content: center; align-items: flex-start; padding-top: 40px; min-height: 100vh; background: white; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
    .qr-card { text-align: center; padding: 24px 28px; border: 2px solid #e5e7eb; border-radius: 12px; display: flex; flex-direction: column; align-items: center; gap: 0; width: fit-content; }
    .hurda-badge { background: #ef4444; color: white; font-size: 18px; font-weight: 800; padding: 6px 20px; border-radius: 6px; letter-spacing: 3px; display: block; margin-bottom: 10px; }
    .inv-no { font-size: 20px; font-weight: 700; color: #1f2937; display: block; margin-bottom: 4px; }
    .brand { font-size: 13px; color: #6b7280; display: block; margin-bottom: 14px; }
    .qr-img { display: block; }
    .form-no { font-size: 11px; color: #9ca3af; display: block; margin-top: 10px; }
    @media print { body { margin: 0; padding-top: 20px; } }
  </style>
</head>
<body>
  <div class="qr-card">
    <span class="hurda-badge">⚠ HURDA</span>
    <span class="inv-no">${selectedQRForm.inventory_number}</span>
    <span class="brand">${selectedQRForm.brand} ${selectedQRForm.model}</span>
    <img class="qr-img" src="${qrDataURL}" alt="QR Kod" width="${qrSize}" height="${qrSize}" />
    <span class="form-no">${selectedQRForm.form_number}</span>
  </div>
</body>
</html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 500);
  };

  const handlePrint = () => {
    if (!selectedForm) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const baseUrl = API_BASE_URL.replace('/api', '');

    const photoHtml = selectedForm.photos.length > 0 ? `
      <div class="section">
        <h3 class="section-title"><span class="badge">5</span> Cihaz Fotoğrafları</h3>
        <div class="photos-grid">
          ${selectedForm.photos.map((photo, i) => `
            <div class="photo-item">
              <img src="${baseUrl}${photo}" alt="Fotoğraf ${i + 1}" />
              <p class="photo-label">Fotoğraf ${i + 1}</p>
            </div>
          `).join('')}
        </div>
      </div>
    ` : '';

    const hwRows = [
      { key: 'cpu', label: 'İşlemci (CPU)' },
      { key: 'ram', label: 'Bellek (RAM)' },
      { key: 'storage', label: 'Depolama' },
      { key: 'screen', label: 'Ekran/Panel' },
      { key: 'motherboard', label: 'Anakart' },
      { key: 'battery', label: 'Batarya' },
      { key: 'adapter', label: 'Adaptör' },
      { key: 'case_condition', label: 'Kasa' },
      { key: 'keyboard', label: 'Klavye/Mousepad' },
      { key: 'ports', label: 'Portlar' }
    ].map(item => {
      const hw = selectedForm.hardware_checklist[item.key as keyof typeof selectedForm.hardware_checklist];
      const status = hw?.status || 'na';
      const statusColors: { [k: string]: string } = {
        exists: '#16a34a', good: '#16a34a', working: '#16a34a',
        missing: '#dc2626', broken: '#dc2626', faulty: '#dc2626', short_circuit: '#dc2626', swollen: '#dc2626',
        dead_pixel: '#ca8a04', stain: '#ca8a04', oxidized: '#ca8a04', not_charging: '#ea580c',
        removed: '#ea580c', incomplete: '#ea580c', na: '#9ca3af'
      };
      const color = statusColors[status] || '#374151';
      return `
        <tr>
          <td>${item.label}</td>
          <td style="color:${color};font-weight:600">${getStatusLabel(status)}</td>
          <td>${hw?.detail || '-'}</td>
          <td style="text-align:center">${hw?.spare_part ? '✓' : '–'}</td>
        </tr>
      `;
    }).join('');

    const reasonsHtml = selectedForm.scrap_reasons
      .map(r => `<span class="tag">${getReasonLabel(r)}</span>`)
      .join('');

    printWindow.document.write(`<!DOCTYPE html>
<html lang="tr">
<head>
  <meta charset="UTF-8">
  <title>Hurda Cihaz Formu - ${selectedForm.form_number}</title>
  <style>
    @page { size: A4; margin: 15mm 15mm 20mm 15mm; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 11px; color: #1f2937; background: white; }

    .header { background: linear-gradient(135deg, #57534e, #44403c); color: white; padding: 14px 18px; border-radius: 8px; margin-bottom: 16px; display: flex; justify-content: space-between; align-items: center; }
    .header-left h1 { font-size: 16px; font-weight: 700; margin-bottom: 3px; }
    .header-left p { font-size: 11px; opacity: 0.85; }
    .header-right { text-align: right; }
    .header-right .form-no { font-size: 13px; font-weight: 700; background: rgba(255,255,255,0.2); padding: 4px 10px; border-radius: 5px; display: inline-block; }
    .header-right .date { font-size: 10px; opacity: 0.8; margin-top: 4px; }
    .hurda-badge { background: #ef4444; color: white; font-size: 10px; font-weight: 800; padding: 2px 8px; border-radius: 4px; letter-spacing: 2px; display: inline-block; margin-top: 6px; }

    .section { margin-bottom: 14px; page-break-inside: avoid; }
    .section-title { font-size: 12px; font-weight: 700; color: #292524; border-bottom: 2px solid #e7e5e4; padding-bottom: 5px; margin-bottom: 8px; display: flex; align-items: center; gap: 6px; }
    .badge { background: #78716c; color: white; font-size: 10px; padding: 1px 7px; border-radius: 4px; }

    .info-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
    .info-item { background: #fafaf9; border: 1px solid #e7e5e4; padding: 7px 10px; border-radius: 5px; }
    .info-item .label { font-size: 9px; color: #78716c; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 2px; }
    .info-item .value { font-size: 11px; font-weight: 600; color: #1c1917; }

    table { width: 100%; border-collapse: collapse; font-size: 10.5px; }
    thead tr { background: #f5f5f4; }
    th { padding: 6px 8px; text-align: left; font-weight: 600; color: #57534e; border: 1px solid #e7e5e4; font-size: 10px; }
    td { padding: 5px 8px; border: 1px solid #e7e5e4; color: #374151; }
    tr:nth-child(even) td { background: #fafaf9; }

    .tags { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }
    .tag { background: #fee2e2; color: #991b1b; font-size: 10px; padding: 3px 10px; border-radius: 20px; font-weight: 500; }
    .note-box { background: #fafaf9; border: 1px solid #e7e5e4; padding: 8px 10px; border-radius: 5px; color: #374151; line-height: 1.5; }
    .note-label { font-size: 9px; color: #78716c; margin-bottom: 3px; }

    .security-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
    .security-item { background: #fafaf9; border: 1px solid #e7e5e4; padding: 7px 10px; border-radius: 5px; }
    .security-item .label { font-size: 9px; color: #78716c; text-transform: uppercase; margin-bottom: 2px; }
    .security-item .value { font-size: 11px; font-weight: 600; }
    .text-green { color: #16a34a; }
    .text-red { color: #dc2626; }

    .photos-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
    .photo-item { border: 1px solid #e7e5e4; border-radius: 6px; overflow: hidden; }
    .photo-item img { width: 100%; height: 110px; object-fit: cover; display: block; }
    .photo-label { text-align: center; font-size: 9px; color: #78716c; padding: 4px; background: #fafaf9; }

    .approval-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
    .sig-box { border: 1px solid #d1d5db; border-radius: 5px; padding: 8px 12px; min-height: 60px; }
    .sig-box .sig-label { font-size: 9px; color: #78716c; text-transform: uppercase; margin-bottom: 4px; }
    .sig-box .sig-name { font-size: 11px; font-weight: 600; color: #1c1917; }
    .sig-box .sig-date { font-size: 10px; color: #6b7280; margin-top: 2px; }
    .sig-line { border-top: 1px dashed #d1d5db; margin-top: 16px; padding-top: 4px; font-size: 9px; color: #9ca3af; text-align: center; }

    .footer { margin-top: 16px; padding-top: 8px; border-top: 1px solid #e7e5e4; text-align: center; font-size: 9px; color: #9ca3af; }
  </style>
</head>
<body>
  <!-- Header -->
  <div class="header">
    <div class="header-left">
      <h1>Hurda Cihaz Bilgi Formu</h1>
      <p>${selectedForm.company_name || 'IT Toolbox'} · ${selectedForm.location_name || '-'}</p>
      <div class="hurda-badge">⚠ HURDA</div>
    </div>
    <div class="header-right">
      <div class="form-no">${selectedForm.form_number}</div>
      <div class="date">Tarih: ${new Date(selectedForm.scrap_date).toLocaleDateString('tr-TR')}</div>
    </div>
  </div>

  <!-- Section 1: Cihaz Bilgileri -->
  <div class="section">
    <h3 class="section-title"><span class="badge">1</span> Cihaz Kimlik Bilgileri</h3>
    <div class="info-grid">
      <div class="info-item"><div class="label">Cihaz Türü</div><div class="value">${getDeviceTypeLabel(selectedForm.device_type)}</div></div>
      <div class="info-item"><div class="label">Envanter No</div><div class="value">${selectedForm.inventory_number}</div></div>
      <div class="info-item"><div class="label">Marka / Model</div><div class="value">${selectedForm.brand} ${selectedForm.model}</div></div>
      <div class="info-item"><div class="label">Seri No</div><div class="value">${selectedForm.serial_number}</div></div>
      <div class="info-item"><div class="label">Alım Tarihi</div><div class="value">${selectedForm.purchase_date ? new Date(selectedForm.purchase_date).toLocaleDateString('tr-TR') : '-'}</div></div>
      <div class="info-item"><div class="label">Şirket</div><div class="value">${selectedForm.company_name || '-'}</div></div>
      <div class="info-item"><div class="label">Lokasyon</div><div class="value">${selectedForm.location_name || '-'}</div></div>
      <div class="info-item"><div class="label">Önceki Kullanıcı</div><div class="value">${selectedForm.previous_user_name || '-'}</div></div>
    </div>
  </div>

  <!-- Section 2: Teknik Donanım -->
  <div class="section">
    <h3 class="section-title"><span class="badge">2</span> Teknik Donanım Durumu</h3>
    <table>
      <thead>
        <tr><th>Parça</th><th>Durum</th><th>Detay</th><th style="width:80px;text-align:center">Yedek Parça?</th></tr>
      </thead>
      <tbody>${hwRows}</tbody>
    </table>
  </div>

  <!-- Section 3: Hurda Nedeni -->
  <div class="section">
    <h3 class="section-title"><span class="badge">3</span> Hurdaya Ayırma Nedeni</h3>
    <div class="tags">${reasonsHtml}</div>
    ${selectedForm.scrap_reason_detail ? `<div class="note-box"><div class="note-label">Açıklama:</div>${selectedForm.scrap_reason_detail}</div>` : ''}
  </div>

  <!-- Section 4: Veri Güvenliği -->
  <div class="section">
    <h3 class="section-title"><span class="badge">4</span> Veri Güvenliği</h3>
    <div class="security-grid">
      <div class="security-item"><div class="label">Disk Söküldü</div><div class="value ${selectedForm.disk_removed ? 'text-green' : 'text-red'}">${selectedForm.disk_removed ? 'Evet' : 'Hayır'}</div></div>
      <div class="security-item"><div class="label">Veri Silme Yöntemi</div><div class="value">${getDataWipeMethodLabel(selectedForm.data_wipe_method)}</div></div>
      <div class="security-item"><div class="label">Medya Kontrolü</div><div class="value ${selectedForm.media_check_done ? 'text-green' : 'text-red'}">${selectedForm.media_check_done ? 'Yapıldı' : 'Yapılmadı'}</div></div>
    </div>
    ${selectedForm.data_security_notes ? `<div class="note-box" style="margin-top:8px"><div class="note-label">Not:</div>${selectedForm.data_security_notes}</div>` : ''}
  </div>

  <!-- Section 5: Fotoğraflar -->
  ${photoHtml}

  <!-- Section 6: Onay -->
  <div class="section">
    <h3 class="section-title"><span class="badge">${selectedForm.photos.length > 0 ? '6' : '5'}</span> Onay Bilgileri</h3>
    <div class="approval-grid">
      <div class="sig-box">
        <div class="sig-label">Formu Dolduran / İmza</div>
        <div class="sig-name">${selectedForm.created_by_user_name}</div>
        <div class="sig-date">${new Date(selectedForm.scrap_date).toLocaleString('tr-TR')}</div>
        <div class="sig-line">İmza</div>
      </div>
      <div class="sig-box">
        <div class="sig-label">Onaylayan Yetkili / İmza</div>
        <div class="sig-name" style="color:#9ca3af">——————————————</div>
        <div class="sig-line">İmza</div>
      </div>
    </div>
  </div>

  <div class="footer">IT Toolbox – Bilgisayar Yönetim Sistemi · ${selectedForm.form_number} · Yazdırma Tarihi: ${new Date().toLocaleString('tr-TR')}</div>
</body>
</html>`);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 600);
  };

  const handleSignedPdfUpload = async () => {
    if (!signedPdfFile || !selectedSignedPdfForm) return;
    setSignedPdfUploading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const formData = new FormData();
      formData.append('signed_pdf', signedPdfFile);
      const response = await fetch(`${API_BASE_URL}/computers/scrap-forms/${selectedSignedPdfForm.id}/upload-signed-pdf`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Yükleme başarısız');
      }
      showGlobalNotification.success('Başarılı', 'İmzalı PDF yüklendi, form onaylandı');
      setShowSignedPdfModal(false);
      setSelectedSignedPdfForm(null);
      setSignedPdfFile(null);
      loadData();
    } catch (error: any) {
      showGlobalNotification.error('Hata', error.message);
    } finally {
      setSignedPdfUploading(false);
    }
  };

  const handleSignedPdfDelete = async (form: ScrapForm) => {
    if (!confirm('İmzalı PDF silinecek. Emin misiniz?')) return;
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/computers/scrap-forms/${form.id}/signed-pdf`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Silme işlemi başarısız');
      showGlobalNotification.success('Başarılı', 'İmzalı PDF silindi');
      loadData();
    } catch (error: any) {
      showGlobalNotification.error('Hata', error.message);
    }
  };

  const handleRestore = async () => {
    if (!formToRestore) return;

    setRestoreLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/computers/scrap-forms/${formToRestore.id}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Geri alma işlemi başarısız');
      }

      showGlobalNotification.success('Başarılı', 'Bilgisayar envantere geri eklendi');
      setShowRestoreModal(false);
      setFormToRestore(null);
      loadData();
    } catch (error: any) {
      showGlobalNotification.error('Hata', error.message);
    } finally {
      setRestoreLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-stone-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <div className="flex items-center gap-4 mb-2">
          <button
            onClick={() => navigateTo('computers')}
            className="p-2 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
            <Trash2 className="mr-2 w-5 h-5 md:w-6 md:h-6 text-stone-600" />
            Hurda Bilgisayarlar
          </h1>
        </div>
        <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 ml-11">
          Toplam {stats?.total || 0} hurda bilgisayar | Bu ay: {stats?.this_month || 0}
        </p>
      </div>

      {/* Stats Cards */}
      {stats && stats.by_location && stats.by_location.length > 0 && (
        <div className="mb-4 md:mb-6">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-5 h-5 text-stone-600" />
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Lokasyonlara Göre Dağılım</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {stats.by_location.map((loc, index) => (
              <div
                key={index}
                onClick={() => setLocationFilter(locationFilter === loc.location_name ? 'all' : loc.location_name)}
                className={`bg-white dark:bg-gray-800 rounded-xl p-4 cursor-pointer transition-all border-2 hover:shadow-lg ${
                  locationFilter === loc.location_name
                    ? 'border-stone-500 ring-2 ring-stone-200 dark:ring-stone-800 shadow-md'
                    : 'border-gray-200 dark:border-gray-700 hover:border-stone-300'
                }`}
              >
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{loc.location_name || 'Belirtilmemiş'}</p>
                <p className="text-2xl font-bold text-stone-600 dark:text-stone-400 mt-1">{loc.count}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-4 md:mb-6 flex flex-wrap gap-2 md:gap-4">
        <div className="flex-1 min-w-[200px] sm:min-w-[300px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Form no, envanter no, marka, model veya seri no ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-stone-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
        </div>

        <div className="flex gap-2">
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            placeholder="Başlangıç"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            placeholder="Bitiş"
          />
        </div>

        {(locationFilter !== 'all' || dateFrom || dateTo || searchTerm) && (
          <button
            onClick={() => {
              setLocationFilter('all');
              setDateFrom('');
              setDateTo('');
              setSearchTerm('');
            }}
            className="px-3 py-2 text-stone-600 hover:bg-stone-100 dark:hover:bg-stone-900/20 rounded-lg flex items-center"
          >
            <X className="w-4 h-4 mr-1" />
            Filtreleri Temizle
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Form No</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Envanter No</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Marka/Model</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Seri No</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Lokasyon</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Hurda Tarihi</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Hurdaya Ayıran</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Onay Durumu</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">İşlemler</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {scrapForms.map((form) => (
                <tr key={form.id} className="hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-stone-600 dark:text-stone-400">
                    {form.form_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {form.inventory_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{form.brand}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{form.model}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {form.serial_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {form.location_name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {new Date(form.scrap_date).toLocaleDateString('tr-TR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {form.created_by_user_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {form.signed_pdf_path ? (
                      <div className="flex flex-col gap-1">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs font-semibold">
                          <FileCheck className="w-3.5 h-3.5" />
                          Onaylandı
                        </span>
                        {form.signed_pdf_uploaded_at && (
                          <span className="text-xs text-gray-400">
                            {new Date(form.signed_pdf_uploaded_at).toLocaleDateString('tr-TR')}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded-full text-xs font-semibold">
                        Bekliyor
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => {
                          setSelectedForm(form);
                          setShowReportModal(true);
                        }}
                        className="text-stone-600 hover:text-stone-900 flex items-center gap-1"
                        title="Rapor Görüntüle"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedQRForm(form);
                          setQrSize(200);
                          setShowQRModal(true);
                        }}
                        className="text-primary hover:text-primary-dark flex items-center gap-1"
                        title="QR Kod Oluştur"
                      >
                        <QrCode className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedSignedPdfForm(form);
                          setSignedPdfFile(null);
                          setShowSignedPdfModal(true);
                        }}
                        className={form.signed_pdf_path ? 'text-green-600 hover:text-green-900' : 'text-purple-600 hover:text-purple-900'}
                        title={form.signed_pdf_path ? 'İmzalı PDF Yönet' : 'İmzalı PDF Yükle'}
                      >
                        <Upload className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => {
                          setFormToRestore(form);
                          setShowRestoreModal(true);
                        }}
                        className="text-green-600 hover:text-green-900 flex items-center gap-1"
                        title="Envantere Geri Ekle"
                      >
                        <RotateCcw className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {scrapForms.length === 0 && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <Trash2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Hurda bilgisayar kaydı bulunamadı</p>
          </div>
        )}
      </div>

      {/* Report Modal */}
      {showReportModal && selectedForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-stone-600 to-stone-700 px-6 py-4 flex items-center justify-between print:bg-stone-600">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-lg">
                  <Trash2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Hurda Cihaz Bilgi Formu</h2>
                  <p className="text-stone-200 text-sm">Form No: {selectedForm.form_number}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrint}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors print:hidden"
                  title="Yazdır"
                >
                  <Printer className="w-5 h-5 text-white" />
                </button>
                <button
                  onClick={() => {
                    setShowReportModal(false);
                    setSelectedForm(null);
                  }}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors print:hidden"
                >
                  <X className="w-6 h-6 text-white" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 print:p-4" id="scrap-report">
              {/* Section 1: Device Info */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2 border-b pb-2">
                  <span className="bg-stone-100 dark:bg-stone-900 text-stone-600 px-2 py-1 rounded text-sm">1</span>
                  Cihaz Kimlik Bilgileri
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Cihaz Türü</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{getDeviceTypeLabel(selectedForm.device_type)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Envanter No</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{selectedForm.inventory_number}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Marka / Model</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{selectedForm.brand} {selectedForm.model}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Seri No</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{selectedForm.serial_number}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Alım Tarihi</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {selectedForm.purchase_date ? new Date(selectedForm.purchase_date).toLocaleDateString('tr-TR') : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Lokasyon</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{selectedForm.location_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Şirket</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{selectedForm.company_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Önceki Kullanıcı</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{selectedForm.previous_user_name || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Section 2: Hardware Checklist */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2 border-b pb-2">
                  <span className="bg-stone-100 dark:bg-stone-900 text-stone-600 px-2 py-1 rounded text-sm">2</span>
                  Teknik Donanım Durumu
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-700">
                        <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400 dark:text-gray-300">Parça</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400 dark:text-gray-300">Durum</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400 dark:text-gray-300">Detay</th>
                        <th className="px-3 py-2 text-center font-medium text-gray-600 dark:text-gray-400 dark:text-gray-300">Yedek Parça?</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {[
                        { key: 'cpu', label: 'İşlemci (CPU)' },
                        { key: 'ram', label: 'Bellek (RAM)' },
                        { key: 'storage', label: 'Depolama' },
                        { key: 'screen', label: 'Ekran/Panel' },
                        { key: 'motherboard', label: 'Anakart' },
                        { key: 'battery', label: 'Batarya' },
                        { key: 'adapter', label: 'Adaptör' },
                        { key: 'case_condition', label: 'Kasa' },
                        { key: 'keyboard', label: 'Klavye/Mousepad' },
                        { key: 'ports', label: 'Portlar' }
                      ].map(item => {
                        const hw = selectedForm.hardware_checklist[item.key as keyof typeof selectedForm.hardware_checklist];
                        return (
                          <tr key={item.key}>
                            <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">{item.label}</td>
                            <td className={`px-3 py-2 ${getStatusColor(hw?.status || 'na')}`}>
                              {getStatusLabel(hw?.status || 'na')}
                            </td>
                            <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{hw?.detail || '-'}</td>
                            <td className="px-3 py-2 text-center">
                              {hw?.spare_part ? (
                                <CheckCircle className="w-5 h-5 text-green-500 inline" />
                              ) : (
                                <XCircle className="w-5 h-5 text-gray-300 inline" />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Section 3: Scrap Reasons */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2 border-b pb-2">
                  <span className="bg-stone-100 dark:bg-stone-900 text-stone-600 px-2 py-1 rounded text-sm">3</span>
                  Hurdaya Ayırma Nedeni
                </h3>
                <div className="flex flex-wrap gap-2 mb-3">
                  {selectedForm.scrap_reasons.map((reason, index) => (
                    <span key={index} className="px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full text-sm">
                      {getReasonLabel(reason)}
                    </span>
                  ))}
                </div>
                {selectedForm.scrap_reason_detail && (
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Açıklama:</p>
                    <p className="text-gray-900 dark:text-gray-100">{selectedForm.scrap_reason_detail}</p>
                  </div>
                )}
              </div>

              {/* Section 4: Data Security */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2 border-b pb-2">
                  <span className="bg-stone-100 dark:bg-stone-900 text-stone-600 px-2 py-1 rounded text-sm">4</span>
                  Veri Güvenliği
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Disk Söküldü</p>
                    <p className={`font-medium ${selectedForm.disk_removed ? 'text-green-600' : 'text-red-600'}`}>
                      {selectedForm.disk_removed ? 'Evet' : 'Hayır'}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Veri Silme Yöntemi</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{getDataWipeMethodLabel(selectedForm.data_wipe_method)}</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Medya Kontrolü</p>
                    <p className={`font-medium ${selectedForm.media_check_done ? 'text-green-600' : 'text-red-600'}`}>
                      {selectedForm.media_check_done ? 'Yapıldı' : 'Yapılmadı'}
                    </p>
                  </div>
                </div>
                {selectedForm.data_security_notes && (
                  <div className="mt-3 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Not:</p>
                    <p className="text-gray-900 dark:text-gray-100">{selectedForm.data_security_notes}</p>
                  </div>
                )}
              </div>

              {/* Section 5: Photos */}
              {selectedForm.photos.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2 border-b pb-2">
                    <span className="bg-stone-100 dark:bg-stone-900 text-stone-600 px-2 py-1 rounded text-sm">5</span>
                    Fotoğraflar
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {selectedForm.photos.map((photo, index) => (
                      <a
                        key={index}
                        href={`${API_BASE_URL.replace('/api', '')}${photo}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <img
                          src={`${API_BASE_URL.replace('/api', '')}${photo}`}
                          alt={`Fotoğraf ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg hover:opacity-80 transition-opacity"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Section 6: Approval Info */}
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                  <span className="bg-stone-100 dark:bg-stone-900 text-stone-600 px-2 py-1 rounded text-sm">6</span>
                  Onay Bilgileri
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Formu Dolduran</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{selectedForm.created_by_user_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">İşlem Tarihi</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {new Date(selectedForm.scrap_date).toLocaleString('tr-TR')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer for print */}
              <div className="mt-8 pt-4 border-t text-center text-sm text-gray-500 dark:text-gray-400 print:block hidden">
                IT Toolbox - Bilgisayar Yönetim Sistemi
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && selectedQRForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-primary to-primary-dark px-5 py-4 flex items-center justify-between rounded-t-xl">
              <div className="flex items-center gap-3">
                <QrCode className="w-5 h-5 text-white" />
                <h2 className="text-lg font-bold text-white">QR Kod</h2>
              </div>
              <button
                onClick={() => { setShowQRModal(false); setSelectedQRForm(null); setQrDataURL(''); }}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="p-6">
              {/* QR Card Preview */}
              <div ref={qrPrintRef} className="text-center p-6 border-2 border-gray-200 dark:border-gray-700 dark:border-gray-600 rounded-xl mb-5">
                <div className="inline-block bg-red-500 text-white text-lg font-black px-5 py-1.5 rounded-md tracking-widest mb-3">
                  ⚠ HURDA
                </div>
                <div className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">
                  {selectedQRForm.inventory_number}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  {selectedQRForm.brand} {selectedQRForm.model}
                </div>
                {qrDataURL ? (
                  <img
                    src={qrDataURL}
                    alt="QR Kod"
                    style={{ width: qrSize, height: qrSize }}
                    className="mx-auto block"
                  />
                ) : (
                  <div
                    style={{ width: qrSize, height: qrSize }}
                    className="mx-auto bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center"
                  >
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                  </div>
                )}
                <div className="text-xs text-gray-400 mt-3">{selectedQRForm.form_number}</div>
              </div>

              {/* Size Control */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  QR Kod Boyutu: <span className="text-primary font-bold">{qrSize}px</span>
                </label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setQrSize(s => Math.max(80, s - 20))}
                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <input
                    type="range"
                    min={80}
                    max={400}
                    step={20}
                    value={qrSize}
                    onChange={(e) => setQrSize(Number(e.target.value))}
                    className="flex-1 accent-blue-600"
                  />
                  <button
                    onClick={() => setQrSize(s => Math.min(400, s + 20))}
                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex justify-between text-xs text-gray-400 mt-1 px-1">
                  <span>Küçük (80px)</span>
                  <span>Büyük (400px)</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowQRModal(false); setSelectedQRForm(null); setQrDataURL(''); }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700"
                >
                  Kapat
                </button>
                <button
                  onClick={handleQRPrint}
                  disabled={!qrDataURL}
                  className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Printer className="w-4 h-4" />
                  Yazdır
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Restore Confirmation Modal */}
      {showRestoreModal && formToRestore && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-full">
                  <RotateCcw className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Envantere Geri Ekle
                </h3>
              </div>

              <div className="mb-6">
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Bu bilgisayarı envantere geri eklemek istediğinize emin misiniz?
                </p>
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg space-y-2">
                  <p className="text-sm">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Form No:</span>{' '}
                    <span className="text-gray-900 dark:text-gray-100">{formToRestore.form_number}</span>
                  </p>
                  <p className="text-sm">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Envanter No:</span>{' '}
                    <span className="text-gray-900 dark:text-gray-100">{formToRestore.inventory_number}</span>
                  </p>
                  <p className="text-sm">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Marka/Model:</span>{' '}
                    <span className="text-gray-900 dark:text-gray-100">{formToRestore.brand} {formToRestore.model}</span>
                  </p>
                </div>
                <p className="text-sm text-orange-600 dark:text-orange-400 mt-4">
                  Not: Hurda raporu ve yüklenen fotoğraflar silinecektir. Bilgisayar durumu "Aktif" olarak güncellenecektir.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowRestoreModal(false);
                    setFormToRestore(null);
                  }}
                  disabled={restoreLoading}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  İptal
                </button>
                <button
                  onClick={handleRestore}
                  disabled={restoreLoading}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {restoreLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      İşleniyor...
                    </>
                  ) : (
                    <>
                      <RotateCcw className="w-4 h-4" />
                      Geri Ekle
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Signed PDF Upload Modal */}
      {showSignedPdfModal && selectedSignedPdfForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 flex items-center justify-between rounded-t-xl">
              <div className="flex items-center gap-3">
                <FileCheck className="w-5 h-5 text-white" />
                <h2 className="text-lg font-bold text-white">Islak İmzalı PDF</h2>
              </div>
              <button
                onClick={() => { setShowSignedPdfModal(false); setSelectedSignedPdfForm(null); setSignedPdfFile(null); }}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            <div className="p-6">
              <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 dark:text-gray-200">{selectedSignedPdfForm.form_number}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{selectedSignedPdfForm.inventory_number} · {selectedSignedPdfForm.brand} {selectedSignedPdfForm.model}</div>
              </div>

              {selectedSignedPdfForm.signed_pdf_path ? (
                <div className="mb-5">
                  <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg mb-3">
                    <FileCheck className="w-5 h-5 text-green-600 dark:text-green-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-green-700 dark:text-green-400">İmzalı PDF mevcut</div>
                      {selectedSignedPdfForm.signed_pdf_uploaded_at && (
                        <div className="text-xs text-green-600 dark:text-green-500">
                          {new Date(selectedSignedPdfForm.signed_pdf_uploaded_at).toLocaleString('tr-TR')}
                          {selectedSignedPdfForm.signed_pdf_uploaded_by && ` · ${selectedSignedPdfForm.signed_pdf_uploaded_by}`}
                        </div>
                      )}
                    </div>
                    <a
                      href={`${API_BASE_URL.replace('/api', '')}${selectedSignedPdfForm.signed_pdf_path}`}
                      target="_blank"
                      rel="noreferrer"
                      className="p-1.5 text-green-600 hover:bg-green-100 dark:hover:bg-green-800/40 rounded"
                      title="İndir / Görüntüle"
                    >
                      <Download className="w-4 h-4" />
                    </a>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Yeni bir PDF yükleyerek mevcut dosyanın üzerine yazabilirsiniz.</p>
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Islak imzalı ve onaylanmış formu PDF olarak yükleyin. Yüklendiğinde form <strong>Onaylandı</strong> olarak işaretlenecektir.
                </p>
              )}

              <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:border-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-colors mb-5">
                <div className="flex flex-col items-center justify-center">
                  <Upload className="w-7 h-7 text-gray-400 mb-2" />
                  {signedPdfFile ? (
                    <span className="text-sm font-medium text-purple-600 dark:text-purple-400">{signedPdfFile.name}</span>
                  ) : (
                    <>
                      <span className="text-sm text-gray-500 dark:text-gray-400">PDF dosyası seçin veya sürükleyin</span>
                      <span className="text-xs text-gray-400 mt-1">Maks. 20 MB</span>
                    </>
                  )}
                </div>
                <input
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={(e) => setSignedPdfFile(e.target.files?.[0] || null)}
                />
              </label>

              <div className="flex gap-3">
                {selectedSignedPdfForm.signed_pdf_path && (
                  <button
                    onClick={() => {
                      handleSignedPdfDelete(selectedSignedPdfForm);
                      setShowSignedPdfModal(false);
                      setSelectedSignedPdfForm(null);
                    }}
                    className="px-4 py-2 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                  >
                    <Trash className="w-4 h-4" />
                    Sil
                  </button>
                )}
                <button
                  onClick={() => { setShowSignedPdfModal(false); setSelectedSignedPdfForm(null); setSignedPdfFile(null); }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700"
                >
                  İptal
                </button>
                <button
                  onClick={handleSignedPdfUpload}
                  disabled={!signedPdfFile || signedPdfUploading}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {signedPdfUploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      Yükleniyor...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Yükle & Onayla
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ScrapComputersPage;
